import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ExportButton } from '@/components/common/ExportButton';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { CitationPicker } from '@/pages/anatel/CitationPicker';
import { ConditionalFinesChart } from '@/pages/anatel/ConditionalFinesChart';
import { fetchCitations, fetchSanctionIntelligence } from '@/services/anatel';

export function AnatelSanctionsPage() {
  const { t, i18n } = useTranslation(['anatel', 'common']);
  const [selected, setSelected] = useState<string[]>([]);
  const [matchAll, setMatchAll] = useState(true);
  const [amountText, setAmountText] = useState('');

  const subjectAmount = Number(amountText.replace(',', '.')) || null;

  const { data: options = [], isPending: optionsPending } = useQuery({
    queryKey: ['anatel', 'citations'],
    queryFn: fetchCitations,
    staleTime: Infinity,
  });

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['anatel', 'sanctions', selected, matchAll, subjectAmount],
    queryFn: () =>
      fetchSanctionIntelligence({
        citations: selected,
        match_all: matchAll,
        subject_amount: subjectAmount,
      }),
    enabled: selected.length > 0,
  });

  const number = new Intl.NumberFormat(i18n.language);

  function toggle(citation: string) {
    setSelected((current) =>
      current.includes(citation)
        ? current.filter((item) => item !== citation)
        : [...current, citation],
    );
  }

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('anatel:sanctions.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('anatel:sanctions.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="anatel:sanctions.explainer" />

      <FieldGuide i18nKey="anatel:sanctions.guide" level={2} />

      {data?.as_of ? <AsOfStrip asOf={data.as_of} /> : null}

      <div className="flex justify-end">
        {/* Nothing to export before a citation is picked: the screen itself does
            not query until then, and an export must be the same query. */}
        <ExportButton
          path="/api/v1/anatel/export/sanctions"
          body={{ citations: selected, match_all: matchAll, subject_amount: subjectAmount }}
          disabled={selected.length === 0}
        />
      </div>

      {optionsPending ? (
        <SkeletonLoader rows={4} />
      ) : (
        <CitationPicker
          options={options}
          selected={selected}
          matchAll={matchAll}
          onToggle={toggle}
          onMatchAllChange={setMatchAll}
        />
      )}

      <label className="block max-w-xs space-y-1">
        <span className="text-xs font-medium text-foreground">
          {t('anatel:sanctions.placeAmount')}
        </span>
        <input
          type="number"
          inputMode="decimal"
          min={0}
          value={amountText}
          placeholder="0,00"
          onChange={(event) => setAmountText(event.target.value)}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
        />
      </label>

      {selected.length === 0 ? (
        <EmptyState hint={t('anatel:sanctions.selectFirst')} />
      ) : isPending ? (
        <SkeletonLoader rows={8} />
      ) : error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : (
        <section className="space-y-3 rounded-lg border bg-card p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-sm font-semibold text-foreground">
              {t('anatel:sanctions.distributionTitle')}
            </h2>
            <p className="text-xs text-muted-foreground">
              {t('anatel:sanctions.matched', {
                n: number.format(data.matched_decisions),
                mode: matchAll ? t('anatel:sanctions.matchAll') : t('anatel:sanctions.matchAny'),
              })}
            </p>
          </div>

          <Answer answer={data.distribution} help={t('anatel:sanctions.help')}>
            {(value) => <ConditionalFinesChart value={value} />}
          </Answer>
        </section>
      )}
    </div>
  );
}
